import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, Eye, ChevronUp, ChevronDown, Play, Pause, CreditCard } from 'lucide-react';
import { useDashboard } from './dashboardContext';
import GenerateFromUrl from './GenerateFromUrl';

export default function TemplatePicker() {
  const { TEMPLATES, activateTpl, activeTab, deletePage, movePage, pages, pausePage, saving, selectPage, sellerAcct, templateLabel } = useDashboard();
  const renderCard = (t) => (
    <motion.div key={t.id} whileHover={{ y: -4 }}
      className="relative rounded-2xl sm:rounded-3xl border p-4 sm:p-6 border-white/10 bg-white/[0.03] flex flex-col"
      data-testid={`template-card-${t.id}`}>
      <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4" style={{ background: `${t.accent}22`, color: t.accent }}>
        <t.icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </span>
      <h3 className="text-white font-semibold text-sm sm:text-lg mb-1 leading-snug" style={{ fontFamily: 'Outfit' }}>{t.title}</h3>
      <p className="text-white/45 text-xs leading-relaxed mb-4 flex-1">{t.desc}</p>
      <button onClick={() => activateTpl(t)} disabled={saving}
        className="w-full h-9 sm:h-10 rounded-full text-xs sm:text-sm font-bold transition-colors bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-50"
        data-testid={`activate-${t.id}`}>
        + Izveidot lapu
      </button>
      {t.group !== 'special' && (
        <a href={`/business-demo/${t.id}`} target="_blank" rel="noreferrer"
          className="mt-2 w-full h-9 rounded-full text-xs font-semibold border border-white/15 text-white/80 hover:bg-white/5 flex items-center justify-center gap-1.5"
          data-testid={`demo-${t.id}`}>
          <Eye className="w-3.5 h-3.5" /> Apskatīt piemēru
        </a>
      )}
    </motion.div>
  );
  return (
    <>
        {activeTab === 'type' && (
        <>
        {/* Active pages — each is an independent landing page with its own
            address, logo and management panel. Sorted to the top. */}
        {pages.length > 0 && (
          <div className="mb-10" data-testid="active-pages-list">
            <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Manas lapas ({pages.length})</h2>
            {sellerAcct && sellerAcct.owed > 0 && (
              <div className="mb-4 rounded-2xl border border-orange-400/30 bg-orange-400/[0.07] px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid="seller-debt-banner">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-orange-400/15 flex items-center justify-center shrink-0"><CreditCard className="w-5 h-5 text-orange-300" /></div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm">KoLV komisijas parāds: €{Number(sellerAcct.owed).toFixed(2)}</p>
                    <p className="text-white/50 text-xs">Visas lapas kopā · {sellerAcct.owed_count} nenokārtoti darījumi. Atver lapas vadību, lai nokārtotu.</p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-3">
              {pages.map((p, idx) => (
                <div key={p.id} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid={`active-page-${p.id}`}>
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => movePage(idx, -1)} disabled={idx === 0}
                        className="w-6 h-6 rounded bg-white/5 text-white/60 hover:text-white disabled:opacity-20 flex items-center justify-center" data-testid={`page-up-${p.id}`}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={() => movePage(idx, 1)} disabled={idx === pages.length - 1}
                        className="w-6 h-6 rounded bg-white/5 text-white/60 hover:text-white disabled:opacity-20 flex items-center justify-center" data-testid={`page-down-${p.id}`}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    {p.display_logo_url
                      ? <img src={p.display_logo_url} alt="" className="w-12 h-12 rounded-xl object-contain bg-white/5 shrink-0" />
                      : <div className="w-12 h-12 rounded-xl bg-[var(--accent-soft)] text-[var(--accent)] flex items-center justify-center shrink-0 font-bold">{(p.display_name || templateLabel(p)).charAt(0)}</div>}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-semibold truncate">{p.display_name || templateLabel(p)}</p>
                        {p.is_paused && <span className="text-[10px] font-bold bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full shrink-0">Pauzēta</span>}
                      </div>
                      {p.display_name && <p className="text-[var(--accent)] text-xs font-medium mt-0.5">{templateLabel(p)}</p>}
                      <p className="text-white/40 text-xs truncate mt-0.5">{p.address || 'Adrese nav norādīta'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto pl-9 sm:pl-0">
                    <button onClick={() => selectPage(p)} className="h-9 px-4 rounded-full bg-[var(--accent)] text-[var(--accent-contrast)] text-sm font-bold hover:bg-[var(--accent-hover)] flex-1 sm:flex-none" data-testid={`manage-page-${p.id}`}>Pārvaldīt</button>
                    <button onClick={() => pausePage(p)} className="h-9 w-9 rounded-full bg-white/5 text-white/60 hover:text-white flex items-center justify-center shrink-0" title={p.is_paused ? 'Aktivizēt' : 'Pauzēt'} data-testid={`pause-page-${p.id}`}>
                      {p.is_paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                    </button>
                    <button onClick={() => deletePage(p)} className="h-9 w-9 rounded-full bg-white/5 text-red-400 hover:bg-red-500/10 flex items-center justify-center shrink-0" title="Dzēst" data-testid={`delete-page-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="text-white font-semibold text-lg mb-3" style={{ fontFamily: 'Outfit' }}>Pievienot jaunu lapu</h2>
        <GenerateFromUrl />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8">
          {TEMPLATES.filter((t) => t.group !== 'special').map(renderCard)}
        </div>

        {TEMPLATES.some((t) => t.group === 'special') && (
          <div className="mb-10 pb-24" data-testid="special-section">
            <h2 className="text-white font-semibold text-lg mb-1 inline-block border-b-2 border-[var(--accent)] pb-0.5" style={{ fontFamily: 'Outfit' }}>Special</h2>
            <p className="text-white/40 text-sm mb-3 mt-1">Pielāgojamas lapas — pats izvēlies, kurus moduļus rādīt. Drīzumā vēl vairāk.</p>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {TEMPLATES.filter((t) => t.group === 'special').map(renderCard)}
            </div>
          </div>
        )}
        </>
        )}
    </>
  );
}
